// Main entry point for the Singlish to Sinhala converter package
import './sinhala-toggle.css';
import { initializeSinhalaInput, resetState } from './sinhala-input.js';

/**
 * Initialize the Singlish to Sinhala converter
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoAddToggle - Automatically add the toggle button (default: true)
 * @param {string} options.togglePosition - Position of toggle ('bottom-right', 'bottom-left', 'top-right', 'top-left') (default: 'bottom-right')
 * @param {boolean} options.startEnabled - Start with conversion enabled (default: false)
 * @returns {Object} - API object with control methods
 */
function initSinglishToSinhala(options = {}) {
  const {
    autoAddToggle = true,
    togglePosition = 'bottom-right',
    startEnabled = false
  } = options;
  
  // Add toggle if requested
  if (autoAddToggle) {
    addToggleToDOM(togglePosition, startEnabled);
  }

  // Initialize the input processor
  initializeSinhalaInput(startEnabled);

  // Return API object
  return {
    enable: () => {
      const toggle = document.getElementById('sinhalaToggle');
      if (toggle) {
        toggle.checked = true;
        toggle.dispatchEvent(new Event('change'));
      }
      localStorage.setItem('sinhalaInputEnabled', 'true');
    },
    disable: () => {
      const toggle = document.getElementById('sinhalaToggle');
      if (toggle) {
        toggle.checked = false;
        toggle.dispatchEvent(new Event('change'));
      }
      localStorage.setItem('sinhalaInputEnabled', 'false');
      resetState();
    },
    isEnabled: () => {
      return localStorage.getItem('sinhalaInputEnabled') === 'true';
    },
    setTypingSpeed: (speed) => {
      // Valid values: 'slow', 'moderate', 'fast'
      if (['slow', 'moderate', 'fast'].includes(speed)) {
        localStorage.setItem('sinhalaTypingSpeed', speed);
        const typingDelay = getTypingDelayFromSpeed(speed);
        updateTypingDelay(typingDelay);
        
        // Update slider if it exists
        const speedSlider = document.getElementById('speedSlider');
        if (speedSlider) {
          let sliderVal = 1; // Default to moderate
          if (speed === 'slow') sliderVal = 0;
          else if (speed === 'fast') sliderVal = 2;
          speedSlider.value = sliderVal;
          updateSpeedLabels(speed);
        }
      }
    },
    getTypingSpeed: () => {
      return localStorage.getItem('sinhalaTypingSpeed') || 'moderate';
    },
    toggle: () => {
      const toggle = document.getElementById('sinhalaToggle');
      if (toggle) {
        toggle.checked = !toggle.checked;
        toggle.dispatchEvent(new Event('change'));
      }
    }
  };
}

// Create and add toggle to DOM
function addToggleToDOM(position = 'bottom-right', startEnabled = false) {
  // Remove existing toggle if present
  const existingToggle = document.getElementById('sinhala-toggle-container');
  if (existingToggle) {
    existingToggle.parentNode.removeChild(existingToggle);
  }

  // Create container
  const container = document.createElement('div');
  container.id = 'sinhala-toggle-container';
  container.className = 'sinhala-toggle-container';
  
  // Position the toggle
  switch (position) {
    case 'top-left':
      container.style.top = '20px';
      container.style.left = '20px';
      container.style.bottom = 'auto';
      container.style.right = 'auto';
      break;
    case 'top-right':
      container.style.top = '20px';
      container.style.right = '20px';
      container.style.bottom = 'auto';
      container.style.left = 'auto';
      break;
    case 'bottom-left':
      container.style.bottom = '20px';
      container.style.left = '20px';
      container.style.top = 'auto';
      container.style.right = 'auto';
      break;
    case 'bottom-right':
    default:
      container.style.bottom = '20px';
      container.style.right = '20px';
      container.style.top = 'auto';
      container.style.left = 'auto';
  }

  // Create toggle content
  container.innerHTML = `
    <div class="toggle-label">සිංහල</div>
    <label class="switch">
      <input type="checkbox" id="sinhalaToggle" ${startEnabled ? 'checked' : ''}>
      <span class="slider round"></span>
    </label>
    <div class="settings-icon" id="sinhalaSettings">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
      </svg>
    </div>
    <div class="speed-settings-popup" id="speedSettingsPopup">
      <div class="speed-settings-content">
        <h4>Typing Speed</h4>
        <div class="speed-slider-container">
          <div class="speed-labels">
            <span class="speed-label" data-speed="slow">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z"/></svg>
              Slow
            </span>
            <span class="speed-label" data-speed="moderate">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              Moderate
            </span>
            <span class="speed-label" data-speed="fast">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M9.99999 6L8.58999 7.41L13.17 12l-4.58001 4.59L9.99999 18l6-6L9.99999 6z"/></svg>
              Fast
            </span>
          </div>
          <input type="range" min="0" max="2" value="1" step="1" id="speedSlider" class="speed-slider">
        </div>
        <div class="speed-description">
          <span id="speedDescription">Moderate Typing Speed</span>
        </div>
      </div>
    </div>
  `;
  
  // Append to document body
  document.body.appendChild(container);

  // Set up the settings icon and popup
  const settingsIcon = document.getElementById('sinhalaSettings');
  const settingsPopup = document.getElementById('speedSettingsPopup');
  const speedSlider = document.getElementById('speedSlider');

  // Initialize slider to saved value or default
  const savedSpeed = localStorage.getItem('sinhalaTypingSpeed') || 'moderate';
  let initialSliderValue = 1; // Default to moderate (slider value 1)
  if (savedSpeed === 'slow') {
    initialSliderValue = 0;
  } else if (savedSpeed === 'fast') {
    initialSliderValue = 2;
  }
  speedSlider.value = initialSliderValue;
  updateSpeedLabels(savedSpeed);

  // Toggle popup when clicking the settings icon
  settingsIcon.addEventListener('click', (e) => {
    e.stopPropagation();
    settingsPopup.classList.toggle('active');
  });

  // Close popup when clicking outside
  document.addEventListener('click', (e) => {
    if (settingsPopup.classList.contains('active') && 
        !settingsPopup.contains(e.target) && 
        e.target !== settingsIcon) {
      settingsPopup.classList.remove('active');
    }
  });

  // Prevent closing when clicking inside the popup
  settingsPopup.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  // Handle slider changes
  document.querySelectorAll('.speed-label').forEach(label => {
    label.addEventListener('click', function() {
      const speed = this.getAttribute('data-speed');
      if (speed === 'slow') speedSlider.value = 0;
      else if (speed === 'moderate') speedSlider.value = 1;
      else if (speed === 'fast') speedSlider.value = 2;
      speedSlider.dispatchEvent(new Event('input'));
    });
  });
  speedSlider.addEventListener('input', function() {
    const value = parseInt(this.value, 10);
    let speed;
    switch (value) {
      case 0: speed = 'slow'; break;
      case 1: speed = 'moderate'; break;
      case 2: speed = 'fast'; break;
      default: speed = 'moderate';
    }
    
    localStorage.setItem('sinhalaTypingSpeed', speed);
    updateSpeedLabels(speed);
    
    // Update the typing delay
    const typingDelay = getTypingDelayFromSpeed(speed);
    updateTypingDelay(typingDelay);
  });

  // Helper function to update visual state of speed labels
  function updateSpeedLabels(speed) {
    const speedLabels = document.querySelectorAll('.speed-label');
    speedLabels.forEach(label => {
      if (label.getAttribute('data-speed') === speed) {
        label.classList.add('active');
      } else {
        label.classList.remove('active');
      }
    });
    
    const speedDescription = document.getElementById('speedDescription');
    switch (speed) {
      case 'slow':
        speedDescription.textContent = 'Slow Typing Speed';
        break;
      case 'moderate':
        speedDescription.textContent = 'Moderate Typing Speed';
        break;
      case 'fast':
        speedDescription.textContent = 'Fast Typing Speed';
        break;
      default:
        speedDescription.textContent = 'Moderate Typing Speed';
        break;
    }
  }

  // Helper function to update typing delay
  function updateTypingDelay(delayMs) {
    if (typeof window !== 'undefined' && window.singlishToSinhala) {
      window.SINHALA_TYPING_DELAY = delayMs;
    }
  }

  // Helper to convert speed setting to ms
  function getTypingDelayFromSpeed(speed) {
    switch (speed) {
      case 'fast': return 250;
      case 'moderate': return 500;
      case 'slow': return 750;
      default: return 500; // Default to moderate
    }
  }
}

// Auto-initialize if window is available and autoInit is not disabled
if (typeof window !== 'undefined' && !window.SINGLISH_NO_AUTO_INIT) {
  // Use DOM ready check before auto-initializing
  function initOnReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        window.singlishToSinhala = initSinglishToSinhala();
      });
    } else {
      window.singlishToSinhala = initSinglishToSinhala();
    }
  }
  
  initOnReady();
}

// Export for different module systems
export default initSinglishToSinhala;

// For CommonJS compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = initSinglishToSinhala;
}